import { LoggerUtil } from '@axiom/ui';

import type { ChatForm as ChatFormValues } from '../../../schema/spark/chat-form-schema';
import { ChatApi } from '../../api/chat-api';
import { ChatErrorUtil } from '../../utils/chat-error-util';
import { ChatLogUtil } from '../../utils/chat-log-util';
import { ChatNoticeUtil } from '../../utils/chat-notice-util';
import type {
  ChatAttachment,
  ChatFailedUpload,
  ChatMessageEntry,
} from '../../types/chat.types';

import type { SparkSession } from './spark-session.types';

/**
 * SparkSendRecoveryUtil - I put the screen back when a send fails. ONLY concerned with the two
 * ways that ends: a stale transcript refreshed, or the prompt handed back with an apology.
 */
export const SparkSendRecoveryUtil = {
  /**
   * Three of the five statuses the api declares reach here, and they want different things said.
   *
   * A lock is busy rather than broken, so it says the words are still in the composer and inviting
   * another press is right. A stopper is not fixable by trying again, so it says come back later and
   * arrives as an error rather than a caution. Anything else keeps the generic apology.
   *
   * The api declares the lock on the upload as well as the send, and this recovery cannot tell which
   * of the two failed - so the verb covers the whole attempt.
   */
  noticeFor({
    attached,
    error,
    failedUpload,
  }: {
    attached: number;
    error: unknown;
    failedUpload: { failures: number; fileName: string } | null;
  }) {
    /*
      First, because it is the only one that can name the file - and with several attached, nothing
      else tells the reader which of them the server would not take.
    */
    if (failedUpload) return ChatNoticeUtil.uploadFailed(failedUpload);

    if (ChatErrorUtil.isLocked({ error })) {
      return ChatNoticeUtil.lockedSend({ attached });
    }

    /*
      Above the stopper because it is a different kind of dead end: the reader can do something about
      this one, and what they can do is the whole point of saying it.
    */
    if (ChatErrorUtil.isContextFull({ error })) {
      return ChatNoticeUtil.contextFull();
    }

    return ChatErrorUtil.isStopper({ error })
      ? ChatNoticeUtil.stopper()
      : ChatNoticeUtil.sendFailed({ attached });
  },

  /** The failed send, undone: the prompt returned, the files returned, and a word on why. */
  async recoverFromSendFailure({
    attachments: staged,
    chat,
    chatId,
    entry,
    error,
    failedUpload = null,
    generation,
    session,
  }: {
    attachments: ChatAttachment[];
    chat: ChatFormValues['chat'];
    chatId: string | null;
    entry: ChatMessageEntry;
    error: unknown;
    /** Set only when the server refused a file - which is the one thing worth naming. */
    failedUpload?: ChatFailedUpload | null;
    generation: number;
    session: SparkSession;
  }) {
    const { chatFormRef, queue, shelf, status, transcript } = session;

    const dropped = queue.abandonQueue();

    /*
      **All of them go back, including any that uploaded before the failure.** The send is abandoned
      whole rather than partly kept, so the reader gets the prompt they wrote with the files they
      chose. The cost is that a retry re-uploads whatever had already landed, leaving those bytes
      orphaned on the server - paid because the alternative silently sends fewer files than were
      attached.
    */
    if (
      staged.length > 0 &&
      transcript.screenGenerationRef.current === generation
    ) {
      shelf.putAttachments([...staged, ...shelf.attachmentsRef.current]);
    }

    if (!ChatErrorUtil.isStale({ error }) || !chatId) {
      /*
        **Put the screen back, then report.** Drop the entry, since it was never accepted,
        return the words, and say why. Reported rather than re-thrown: a throw from here is
        awaited by `onSubmit`, and Formik's `submitForm` catches it into a `console.warn`, so
        it never reached the global handler it was meant for.
      */
      if (transcript.screenGenerationRef.current === generation) {
        /*
          **The upload may have minted a conversation before the failure**, and only the ref knew:
          the state and `loadedChatIdRef` are set after the send, past the throw. Left half-set the
          screen reads "new chat" while the ref points at a real one.

          `loadedChatIdRef` too, so the loader in `useSparkConversation` does not fetch it -
          nothing is showable until the LLM has answered, so there would be nothing to paint.
        */
        if (!chatId && transcript.activeChatIdRef.current) {
          transcript.loadedChatIdRef.current =
            transcript.activeChatIdRef.current;
          transcript.setActiveChatId(transcript.activeChatIdRef.current);
        }

        transcript.setChatLog(previous =>
          previous.filter(logEntry => logEntry.id !== entry.id)
        );
        transcript.setPinnedPromptId(pinned =>
          pinned === entry.id ? null : pinned
        );

        await chatFormRef.current?.setFieldValue('chat', chat);

        /*
          **Before the notice, and inside the generation guard.** The composer is shut for the rest
          of this conversation, so the words go back into a field the reader can copy from but not
          send - and marking a chat the reader has already left would shut the one they are in now.
        */
        if (ChatErrorUtil.isContextFull({ error })) transcript.markChatFull();

        status.setNotice(
          SparkSendRecoveryUtil.noticeFor({
            attached: staged.length,
            error,
            failedUpload,
          })
        );
      }

      LoggerUtil.fireError(
        `Spark: failed to send prompt - ${
          error instanceof Error ? error.message : JSON.stringify(error)
        }`
      );

      return;
    }

    // `loadChat` invalidates first, or the recovery would paint back the very copy the 409 refused.
    const { data: refreshed } = await ChatApi.loadChat({ chatId });

    if (transcript.screenGenerationRef.current !== generation) return;

    transcript.loadedChatIdRef.current = refreshed.id;
    transcript.setChatLog(
      ChatLogUtil.toEntries({
        chatId: refreshed.id,
        messages: refreshed.messages,
        openAiFiles: refreshed.openAiFiles,
      })
    );
    transcript.showMatchingCandidates(
      ChatLogUtil.latestMatchingCandidates({
        messages: refreshed.messages,
      })
    );

    transcript.lastAnswerIdRef.current = ChatLogUtil.latestAnswerId({
      messages: refreshed.messages,
    });

    transcript.setPinnedPromptId(null);
    transcript.setRestoredChatId(refreshed.id);

    /*
      The prompt goes back where it came from so it can be revised against what is now on screen.
      Set rather than left alone: the submit's `resetForm` already emptied the composer as the
      prompt was sent.
    */
    await chatFormRef.current?.setFieldValue('chat', chat);

    // Its file went back at the top of this recovery, along with every other path's.

    /*
      **The prompt is named as well as the refresh.** It never reached the server, and the composer
      it went back into is below the fold of a long conversation - so without saying so the reader is
      left to guess whether to retype it.
    */
    status.setNotice(
      dropped > 0
        ? `We saw that your chat was behind and refreshed it for you. Your prompt was never sent, and is back in the composer. ${ChatNoticeUtil.droppedPrompts(
            { dropped }
          )}.`
        : 'We saw that your chat was behind and refreshed it for you. Your prompt was never sent, and is back in the composer.'
    );
  },
};
